import React, { useState, useEffect } from 'react';
import { Video, Save, AlertCircle, CheckCircle, Link as LinkIcon } from 'lucide-react';

const AdminWelcomeVideo = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchCurrentVideo();
  }, []);

  const fetchCurrentVideo = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/settings/welcome-video`);
      const data = await response.json();
      
      if (data.video_url) {
        setCurrentVideoUrl(data.video_url);
        setVideoUrl(data.video_url);
        setPreviewUrl(getEmbedUrl(data.video_url));
      }
    } catch (error) {
      console.error('Error fetching welcome video:', error);
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : new URLSearchParams(new URL(url).search).get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : '';
    }
    
    // Direct video file
    if (url.match(/\.(mp4|webm|ogg)$/i)) {
      return url;
    }
    
    return '';
  };

  const handlePreview = () => {
    const embedUrl = getEmbedUrl(videoUrl);
    if (embedUrl) {
      setPreviewUrl(embedUrl);
      setMessage({ type: '', text: '' });
    } else {
      setMessage({ type: 'error', text: 'Invalid video URL. Please use YouTube, Vimeo, or direct video file URL.' });
      setPreviewUrl('');
    }
  };

  const handleSave = async () => {
    if (!videoUrl.trim()) {
      setMessage({ type: 'error', text: 'Please enter a video URL' });
      return;
    }

    const embedUrl = getEmbedUrl(videoUrl);
    if (!embedUrl) {
      setMessage({ type: 'error', text: 'Invalid video URL format' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/settings/welcome-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ video_url: videoUrl })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Welcome video updated successfully!' });
        setCurrentVideoUrl(videoUrl);
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update video' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const isDirectVideo = previewUrl && previewUrl.match(/\.(mp4|webm|ogg)$/i);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-purple-100 rounded-xl">
          <Video className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Welcome Video</h2>
          <p className="text-sm text-gray-600">Set a personal welcome video for your students</p>
        </div>
      </div>

      {/* Video URL Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Video URL
        </label>
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <LinkIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handlePreview}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Preview
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Supported: YouTube, Vimeo, or direct video file URLs (.mp4, .webm, .ogg)
        </p>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Video Preview */}
      {previewUrl && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preview
          </label>
          <div className="aspect-video bg-black rounded-xl overflow-hidden">
            {isDirectVideo ? (
              <video
                src={previewUrl}
                controls
                className="w-full h-full"
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <iframe
                src={previewUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Welcome Video Preview"
              ></iframe>
            )}
          </div>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading || !videoUrl.trim()}
        className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>Saving...</span>
          </>
        ) : (
          <>
            <Save className="w-5 h-5" />
            <span>Save Welcome Video</span>
          </>
        )}
      </button>

      {/* Current Video Info */}
      {currentVideoUrl && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Currently Active Video:</p>
          <p className="text-sm text-gray-900 font-mono break-all">{currentVideoUrl}</p>
        </div>
      )}
    </div>
  );
};

export default AdminWelcomeVideo;
